// Cliente HTTP de la liquidacion con montos (LQ-4), mismo patron que
// settlements/http.ts: token en el header, sin cookies ni redirecciones, y el
// JSON por el parser de @ucorp/contracts antes de tocar estado. El error sale
// tipado por status (y por el tipo del sobre en los 409), nunca con el
// cuerpo remoto; el 409 settlement_not_full es un resultado (la planta liquida
// sin montos), no una falla. LQ-6: la conformidad y la observacion son POST
// que responden la revision del periodo, sin montos.
import { ProviderSettlementsResponse, SettlementDraft, SettlementReview } from "@ucorp/contracts";
import type { PortalSession } from "../auth/session.js";
import { approvePath, disputePath, settlementDraftPath, settlementSummaryPath } from "../logic/settlement.js";
import { fileNameFromDisposition } from "../logic/trip-export.js";
import { SettlementError, type SettlementPort } from "./settlement-port.js";

/** El tipo del sobre de error, solo para distinguir los 409; el detail no se lee ni se muestra. */
async function errorType(response: Response): Promise<string | undefined> {
  try {
    const body = (await response.json()) as { error?: { type?: unknown } };
    return typeof body.error?.type === "string" ? body.error.type : undefined;
  } catch {
    return undefined;
  }
}

async function failure(response: Response): Promise<SettlementError> {
  const status = response.status;
  if (status === 403) return new SettlementError("access");
  if (status === 401) return new SettlementError("session");
  if (status === 400) return new SettlementError("validation");
  if (status === 409) {
    const type = await errorType(response);
    if (type === "settlement_period_state") return new SettlementError("period_state");
    if (type === "settlement_draft_changed") return new SettlementError("draft_changed");
    return new SettlementError("not_full");
  }
  return new SettlementError("unavailable");
}

export function createHttpSettlementPort(apiUrl: string, request: typeof fetch = fetch): SettlementPort {
  const base = apiUrl.replace(/\/+$/, "");

  async function get(session: PortalSession, path: string): Promise<Response> {
    let response: Response;
    try {
      response = await request(`${base}${path}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${session.accessToken}` },
        cache: "no-store",
        credentials: "omit",
        redirect: "error",
      });
    } catch {
      throw new SettlementError("unavailable");
    }
    if (!response.ok) throw await failure(response);
    return response;
  }

  async function post(session: PortalSession, path: string, body: unknown): Promise<Response> {
    let response: Response;
    try {
      response = await request(`${base}${path}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${session.accessToken}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
        cache: "no-store",
        credentials: "omit",
        redirect: "error",
      });
    } catch {
      throw new SettlementError("unavailable");
    }
    if (!response.ok) throw await failure(response);
    return response;
  }

  async function review(response: Response): Promise<SettlementReview> {
    const parsed = SettlementReview.safeParse(await json(response));
    if (!parsed.success) throw new SettlementError("unavailable");
    return parsed.data;
  }

  async function json(response: Response): Promise<unknown> {
    try {
      return (await response.json()) as unknown;
    } catch {
      throw new SettlementError("unavailable");
    }
  }

  return {
    async summary(session, date) {
      const parsed = ProviderSettlementsResponse.safeParse(await json(await get(session, settlementSummaryPath(date))));
      if (!parsed.success) throw new SettlementError("unavailable");
      return parsed.data;
    },
    async draft(session, siteId, date) {
      const parsed = SettlementDraft.safeParse(await json(await get(session, settlementDraftPath(siteId, date, "json"))));
      // Un borrador de otra planta es una respuesta rota.
      if (!parsed.success || parsed.data.siteId !== siteId) throw new SettlementError("unavailable");
      return parsed.data;
    },
    async download(session, siteId, date, format) {
      const response = await get(session, settlementDraftPath(siteId, date, format));
      let blob: Blob;
      try {
        blob = await response.blob();
      } catch {
        throw new SettlementError("unavailable");
      }
      return { blob, fileName: fileNameFromDisposition(response.headers.get("Content-Disposition"), siteId, date, format).replace(/^viajes-/, "liquidacion-") };
    },
    async approve(session, siteId, periodId, inputsHash) {
      return review(await post(session, approvePath(siteId, periodId), { inputsHash }));
    },
    async dispute(session, siteId, periodId, request) {
      return review(await post(session, disputePath(siteId, periodId), request));
    },
  };
}
